// Offline play (spec/13 §5.4; spec/05 §8.4a, §8.5; the owner's offline-play decision of 2026-09-30): what staff need
// when the venue's PCs lose Clavian.
//
//   Staff codes        issue one-time codes (N minutes, valid for H hours) and print them as a sheet of cut-out
//                      cards; each starts one session on any PC while it is offline. The digits are shown once — the
//                      server keeps only a salted hash — and a PC learns new codes within five minutes while online,
//                      so a sheet is printed ahead of an outage. Revoke one that is lost.
//   Offline sessions   what the PCs ran offline, as they reported on reconnect. A known player's is charged to them
//                      and settled; a staff code's waits here for staff to record it paid at the desk (take the payment
//                      through Sell) or comp — offline play is never silently free.
//
// Which options a venue uses is its Offline play settings group; this page links there.

import type { OfflineSession, OfflineStaffCode } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, EmptyState, SegmentedControl, StatusPill, Table, TextField, useToast, VisuallyHidden, type TableColumn } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { useAccess } from '../../api/queries';
import { useProblem } from '../../api/use-problem';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { useStations } from '../../stations/queries';
import { Forbidden, Notice, Page, RowActions, Section, TableSkeleton } from '../../ui';
import { formatDuration, formatMoney } from './money';
import {
  useIssueOfflineStaffCodes,
  useOfflineSessions,
  useOfflineStaffCodes,
  useResolveOfflineSession,
  useRevokeOfflineStaffCode,
} from './offline-queries';
import { SessionPlayer } from './SessionPlayer';

import './sessions.css';

const SESSIONS_READ = 'sessions:read';
const SESSIONS_OFFLINE_CODE = 'sessions:offline_code';
const PLAYERS_READ = 'players:read';

type ConsoleT = (key: string, options?: Record<string, unknown>) => string;

const when = (iso: string | null): string =>
  iso === null ? '' : new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso));

export function OfflinePlayPage(): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const access = useAccess({ venueId: venue.id });
  const canRead = access.status === 'ready' && access.can(SESSIONS_READ);
  const canIssue = access.status === 'ready' && access.can(SESSIONS_OFFLINE_CODE);
  const canReadPlayers = access.status === 'ready' && access.can(PLAYERS_READ);

  const codes = useOfflineStaffCodes(venue.organizationId, venue.id, canRead);
  const sessions = useOfflineSessions(venue.organizationId, venue.id, canRead);
  const stations = useStations(venue.organizationId, venue.id);
  const stationName = useMemo(() => {
    const byId = new Map<string, string>();
    for (const station of stations.data?.pages.flatMap((page) => page.data) ?? []) {
      byId.set(station.id, station.name ?? t('floor.tile.numbered', { number: station.number }));
    }
    return (id: string | null): string => (id === null ? '' : (byId.get(id) ?? t('sessions.offline.unknownStation')));
  }, [stations.data, t]);

  if (access.status === 'ready' && !canRead) {
    return (
      <Page title={t('sessions.offline.title')}>
        <Forbidden message={t('sessions.offline.forbidden')} />
      </Page>
    );
  }

  return (
    <Page
      title={t('sessions.offline.title')}
      description={t('sessions.offline.intro')}
      actions={
        <Button asChild variant="secondary">
          <Link to="/v/$venueSlug/settings/$group" params={{ venueSlug: venue.slug, group: 'sessionsAndPcs' }} hash="offline.continueRunning">
            {t('sessions.offline.settingsLink')}
          </Link>
        </Button>
      }
    >
      <OfflineSessionsSection
        sessions={sessions}
        stationName={stationName}
        canResolve={canIssue}
        canReadPlayers={canReadPlayers}
        organizationId={venue.organizationId}
        venueId={venue.id}
      />
      <StaffCodesSection codes={codes} stationName={stationName} canIssue={canIssue} organizationId={venue.organizationId} venueId={venue.id} venueName={venue.name} />
    </Page>
  );
}

// ---------------------------------------------------------------------------------------------
// Offline sessions
// ---------------------------------------------------------------------------------------------

function OfflineSessionsSection({
  sessions,
  stationName,
  canResolve,
  canReadPlayers,
  organizationId,
  venueId,
}: {
  readonly sessions: ReturnType<typeof useOfflineSessions>;
  readonly stationName: (id: string | null) => string;
  readonly canResolve: boolean;
  readonly canReadPlayers: boolean;
  readonly organizationId: string;
  readonly venueId: string;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const resolve = useResolveOfflineSession(organizationId, venueId);
  const rows = sessions.data ?? [];
  const pending = rows.filter((row) => row.status === 'needs_attention').length;

  const settle = (row: OfflineSession, resolution: 'paid_at_desk' | 'comp'): void => {
    resolve.mutate(
      { offlineSessionId: row.id, resolution },
      {
        onSuccess: () => show({ tone: 'ok', title: t(resolution === 'comp' ? 'sessions.offline.resolved.comp' : 'sessions.offline.resolved.paid') }),
        onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('sessions.offline.resolveFailed') }) }),
      },
    );
  };

  const columns: readonly TableColumn<OfflineSession>[] = [
    { key: 'station', header: t('sessions.offline.column.station'), cell: (row) => stationName(row.stationId) },
    {
      key: 'who',
      header: t('sessions.offline.column.who'),
      cell: (row) =>
        row.playerId === null ? (
          <span>{t('sessions.offline.kind.staffCode')}</span>
        ) : (
          <SessionPlayer organizationId={organizationId} playerId={row.playerId} canReadPlayers={canReadPlayers} />
        ),
    },
    { key: 'when', header: t('sessions.offline.column.when'), cell: (row) => `${when(row.startedAt)} – ${when(row.endedAt)}` },
    { key: 'played', header: t('sessions.offline.column.played'), numeric: true, cell: (row) => formatDuration(row.playedSeconds) },
    {
      key: 'charged',
      header: t('sessions.offline.column.charged'),
      numeric: true,
      cell: (row) => (row.chargedSeconds > 0 ? formatMoney(row.charge) : '—'),
    },
    { key: 'status', header: t('sessions.offline.column.status'), cell: (row) => <OfflineSessionStatus row={row} t={t} /> },
    {
      key: 'actions',
      header: <VisuallyHidden>{t('common.actions')}</VisuallyHidden>,
      cell: (row) =>
        canResolve && row.status === 'needs_attention' ? (
          <RowActions
            name={stationName(row.stationId)}
            items={[
              { id: 'paid', label: t('sessions.offline.action.paid'), onSelect: () => settle(row, 'paid_at_desk') },
              { id: 'comp', label: t('sessions.offline.action.comp'), onSelect: () => settle(row, 'comp') },
            ]}
          />
        ) : null,
    },
  ];

  let body: ReactNode;
  if (sessions.isError) body = <LoadFailure error={sessions.error} message={t('sessions.offline.unavailable')} onRetry={() => void sessions.refetch()} />;
  else if (sessions.isPending) body = <TableSkeleton rows={3} />;
  else if (rows.length === 0) body = <EmptyState icon={<Icon name="timer" />} title={t('sessions.offline.sessionsEmptyTitle')} description={t('sessions.offline.sessionsEmpty')} />;
  else {
    body = (
      <div className="console-table-card">
        <Table
          caption={t('sessions.offline.sessionsTitle')}
          captionHidden
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          rowTone={(row) => (row.status === 'needs_attention' ? 'warn' : undefined)}
        />
      </div>
    );
  }

  return (
    <Section
      title={t('sessions.offline.sessionsTitle')}
      description={pending > 0 ? t('sessions.offline.sessionsPending', { count: pending }) : t('sessions.offline.sessionsIntro')}
      body="rows"
    >
      {body}
    </Section>
  );
}

function OfflineSessionStatus({ row, t }: { readonly row: OfflineSession; readonly t: ConsoleT }): ReactNode {
  if (row.status === 'settled') {
    return (
      <StatusPill state="free" size="sm">
        {t(`sessions.offline.resolution.${row.resolution ?? 'charged'}`)}
      </StatusPill>
    );
  }
  return (
    <StatusPill state="ending" size="sm">
      {t(`sessions.offline.attention.${row.attention ?? 'staff_code'}`)}
    </StatusPill>
  );
}

// ---------------------------------------------------------------------------------------------
// Staff codes
// ---------------------------------------------------------------------------------------------

function StaffCodesSection({
  codes,
  stationName,
  canIssue,
  organizationId,
  venueId,
  venueName,
}: {
  readonly codes: ReturnType<typeof useOfflineStaffCodes>;
  readonly stationName: (id: string | null) => string;
  readonly canIssue: boolean;
  readonly organizationId: string;
  readonly venueId: string;
  readonly venueName: string;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const issue = useIssueOfflineStaffCodes(organizationId, venueId);
  const revoke = useRevokeOfflineStaffCode(organizationId, venueId);
  const [count, setCount] = useState('10');
  const [minutes, setMinutes] = useState('60');
  const [validHours, setValidHours] = useState('72');
  const [note, setNote] = useState('');
  const [kind, setKind] = useState<'session' | 'maintenance'>('session');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [issued, setIssued] = useState<readonly { readonly code: string; readonly minutes: number; readonly expiresAt: string }[] | null>(null);

  const whole = (text: string, min: number, max: number): number | null => {
    const value = Number(text);
    return text.trim() !== '' && Number.isInteger(value) && value >= min && value <= max ? value : null;
  };

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const next: Record<string, string> = {};
    const c = whole(count, 1, 50);
    const m = whole(minutes, 5, kind === 'maintenance' ? 240 : 480);
    const h = whole(validHours, 1, 720);
    if (c === null) next['count'] = t('sessions.offline.issue.countRange');
    if (m === null) next['minutes'] = kind === 'maintenance' ? t('sessions.offline.issue.maintenanceRange') : t('sessions.offline.issue.minutesRange');
    if (h === null) next['validHours'] = t('sessions.offline.issue.hoursRange');
    setErrors(next);
    if (c === null || m === null || h === null) return;
    issue.mutate(
      { count: c, minutes: m, validHours: h, kind, ...(note.trim() === '' ? {} : { note: note.trim() }) },
      {
        onSuccess: (result) => setIssued(result.codes),
        onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('sessions.offline.issue.failed') }) }),
      },
    );
  };

  const columns: readonly TableColumn<OfflineStaffCode>[] = [
    { key: 'minutes', header: t('sessions.offline.codes.column.minutes'), numeric: true, cell: (row) => t('sessions.offline.codes.minutes', { minutes: row.minutes }) },
    { key: 'issued', header: t('sessions.offline.codes.column.issued'), cell: (row) => when(row.issuedAt) },
    { key: 'expires', header: t('sessions.offline.codes.column.expires'), cell: (row) => when(row.expiresAt) },
    {
      key: 'status',
      header: t('sessions.offline.codes.column.status'),
      cell: (row) => (
        <StatusPill state={row.status === 'live' ? 'session' : row.status === 'used' ? 'free' : 'offline'} size="sm">
          {row.status === 'used' ? t('sessions.offline.codes.usedOn', { station: stationName(row.usedStationId) }) : t(`sessions.offline.codes.status.${row.status}`)}
        </StatusPill>
      ),
    },
    { key: 'note', header: t('sessions.offline.codes.column.note'), cell: (row) => row.note ?? '' },
    {
      key: 'actions',
      header: <VisuallyHidden>{t('common.actions')}</VisuallyHidden>,
      cell: (row) =>
        canIssue && row.status === 'live' ? (
          <RowActions
            name={t('sessions.offline.codes.rowName', { minutes: row.minutes })}
            items={[
              {
                id: 'revoke',
                label: t('sessions.offline.codes.revoke'),
                onSelect: () => revoke.mutate(row.id, { onSuccess: () => show({ tone: 'ok', title: t('sessions.offline.codes.revoked') }) }),
              },
            ]}
          />
        ) : null,
    },
  ];

  const rows = codes.data ?? [];
  let list: ReactNode;
  if (codes.isError) list = <LoadFailure error={codes.error} message={t('sessions.offline.codes.unavailable')} onRetry={() => void codes.refetch()} />;
  else if (codes.isPending) list = <TableSkeleton rows={3} />;
  else if (rows.length === 0) list = <p className="console-subtle">{t('sessions.offline.codes.empty')}</p>;
  else {
    list = (
      <div className="console-table-card">
        <Table
          caption={t('sessions.offline.codes.title')}
          captionHidden
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          rowTone={(row) => (row.status === 'live' ? 'live' : row.status === 'used' ? undefined : 'quiet')}
        />
      </div>
    );
  }

  return (
    <Section title={t('sessions.offline.codes.title')} description={t('sessions.offline.codes.intro')}>
      {canIssue ? (
        issued === null ? (
          <form className="console-offline-issue" onSubmit={submit} noValidate>
            <SegmentedControl
              size="sm"
              label={t('sessions.offline.issue.kind')}
              value={kind}
              onValueChange={(value) => setKind(value === 'maintenance' ? 'maintenance' : 'session')}
              options={(['session', 'maintenance'] as const).map((value) => ({ value, label: t(`sessions.offline.issue.kinds.${value}`) }))}
            />
            <TextField label={t('sessions.offline.issue.count')} inputMode="numeric" value={count} onChange={(event) => setCount(event.target.value)} error={errors['count']} />
            <TextField label={t('sessions.offline.issue.minutes')} inputMode="numeric" value={minutes} onChange={(event) => setMinutes(event.target.value)} error={errors['minutes']} />
            <TextField
              label={t('sessions.offline.issue.validHours')}
              inputMode="numeric"
              value={validHours}
              onChange={(event) => setValidHours(event.target.value)}
              error={errors['validHours']}
            />
            <TextField label={t('sessions.offline.issue.note')} value={note} onChange={(event) => setNote(event.target.value)} />
            <Button type="submit" variant="primary" busy={issue.isPending}>
              {t('sessions.offline.issue.submit')}
            </Button>
          </form>
        ) : (
          <Notice
            tone="ok"
            role="status"
            title={t('sessions.offline.issue.done', { count: issued.length })}
            action={
              <>
                <Button variant="primary" icon={<Icon name="print" />} onClick={() => openCodeSheet(issued, venueName, t)}>
                  {t('sessions.offline.issue.print')}
                </Button>
                <Button variant="ghost" onClick={() => setIssued(null)}>
                  {t('sessions.offline.issue.close')}
                </Button>
              </>
            }
          >
            <p>{t('sessions.offline.issue.onceOnly')}</p>
            <ul className="console-offline-codes" aria-label={t('sessions.offline.issue.listLabel')}>
              {issued.map((entry) => (
                <li key={entry.code} className="clav-num">
                  {groupDigits(entry.code)} · {t('sessions.offline.codes.minutes', { minutes: entry.minutes })}
                </li>
              ))}
            </ul>
          </Notice>
        )
      ) : null}
      {list}
    </Section>
  );
}

/** "4081 7263": the 8 digits in two groups of four, easier to read out and type. */
export function groupDigits(code: string): string {
  return code.length === 8 ? `${code.slice(0, 4)} ${code.slice(4)}` : code;
}

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] ?? char);

/**
 * The printable sheet (spec/13 §5.4): one cut-out card per code — the venue, the minutes, the code in large digits and
 * when it expires — for the front desk to hand out while the PCs are offline. Black on white paper, so it names its own
 * ink rather than the console's tokens. The digits exist only in this window and the response they came from.
 */
function openCodeSheet(codes: readonly { readonly code: string; readonly minutes: number; readonly expiresAt: string }[], venueName: string, t: ConsoleT): void {
  const win = window.open('', '_blank', 'width=800,height=900');
  if (win === null) return; // A popup blocker stopped it; the codes stay on the page.
  const cards = codes
    .map(
      (entry) =>
        `<div class="card"><div class="venue">${escapeHtml(venueName)}</div>` +
        `<div class="minutes">${escapeHtml(t('sessions.offline.sheet.minutes', { minutes: entry.minutes }))}</div>` +
        `<div class="code">${escapeHtml(groupDigits(entry.code))}</div>` +
        `<div class="how">${escapeHtml(t('sessions.offline.sheet.how'))}</div>` +
        `<div class="expires">${escapeHtml(t('sessions.offline.sheet.expires', { when: when(entry.expiresAt) }))}</div></div>`,
    )
    .join('');
  win.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(t('sessions.offline.sheet.title'))}</title><style>` +
      '@page{size:A4;margin:10mm}body{font-family:system-ui,sans-serif;color:#000;margin:0}' +
      '.grid{display:grid;grid-template-columns:repeat(2,1fr);gap:0}' +
      '.card{border:1px dashed #000;padding:6mm;break-inside:avoid;min-height:40mm}' +
      '.venue{font-size:12px;text-transform:uppercase;letter-spacing:1px}.minutes{font-size:14px;margin-top:2mm}' +
      '.code{font-family:ui-monospace,monospace;font-size:30px;font-weight:700;letter-spacing:3px;margin:3mm 0}' +
      '.how,.expires{font-size:11px}' +
      `</style></head><body onload="window.print()"><div class="grid">${cards}</div></body></html>`,
  );
  win.document.close();
}
